import React from 'react';
import { StyleSheet, View, type ViewProps } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';

import { useResponsive } from '../../../../hooks';

import type { PaletteProps } from '../../Modal.types';

export const ModalContent = (props: ModalContentProps) => {
  const {
    children,
    palette,
    shouldFitToFill,
    withSafeAreaView = true,
    style,
    ...restProps
  } = props;
  const { currentBreakpoint } = useResponsive();
  const insets = useSafeAreaInsets();

  const responsiveStyles = currentBreakpoint.isXS ? styles.contentMobile : styles.contentTabletUp;
  const sizeBasedStyles = shouldFitToFill ? styles.contentFitToFill : responsiveStyles;

  // @ts-expect-error error TS7053: Element implicitly has an 'any' type becau
  const paletteSpecificStyles = palette ? modalPaletteStyles[palette] : {};

  const safeAreaPaddingStyle = useStyle(() => {
    return withSafeAreaView ? { paddingBottom: insets.bottom } : undefined;
  }, [withSafeAreaView, insets.bottom]);

  return (
    <View
      style={[styles.content, sizeBasedStyles, paletteSpecificStyles, safeAreaPaddingStyle, style]}
      {...restProps}
    >
      {children}
    </View>
  );
};

// ─── Types ─────────────────────────────────────────────────────────────

type ModalContentProps = ViewProps &
  PaletteProps &
  Readonly<{
    /**
     * Determines whether modal content should flex grow.
     */
    shouldFitToFill: boolean;

    /**
     * Determines whether modal content should be wrapped in the `SafeArea` container.
     */
    withSafeAreaView?: boolean;
  }>;

// ─── Styles ────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  content: {
    backgroundColor: theme.colors.APP_BACKGROUND,
    maxHeight: '100%',
    overflow: 'hidden',
  },
  contentMobile: {
    ...theme.elevations['2'],
    borderTopRightRadius: theme.radius.large,
    borderTopLeftRadius: theme.radius.large,
  },
  contentTabletUp: {
    ...theme.elevations['3'],
    borderRadius: theme.radius.large,
  },
  contentFitToFill: {
    flexGrow: 1,
  },
  paletteSecondary: {
    backgroundColor: theme.colors.SPINACH,
  },
  paletteLime: {
    backgroundColor: theme.colors.LIME,
  },
  paletteForest: {
    backgroundColor: theme.colors.FOREST,
  },
  paletteDarkKale: {
    backgroundColor: theme.colors.DARK_KALE,
  },
});

const modalPaletteStyles = {
  primary: undefined,
  neutral: undefined,
  secondary: styles.paletteSecondary,
  lime: styles.paletteLime,
  forest: styles.paletteForest,
  'dark-kale': styles.paletteDarkKale,
};
